import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "./Navbar";
import { supabase } from "./supabase";

const inputStyle = {
  width: "100%",
  background: "#F5F0E8",
  border: "1.5px solid #EDE8DF",
  borderRadius: 8,
  padding: "11px 14px",
  color: "#1A1A2E",
  fontSize: 16,
  fontFamily: "var(--font-body)",
  outline: "none",
  marginBottom: 16,
  boxSizing: "border-box",
};

const labelStyle = {
  fontSize: 11,
  color: "#888",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 6,
  display: "block",
};

export default function SubmitMilestone() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [myProjects, setMyProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [selectedProject, setSelectedProject] = useState("");
  const [form, setForm] = useState({ title: "", description: "", stage_number: "", approved_budget: "", supplier_id: "", invoice_amount: "", invoice_reference: "" });
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceType, setEvidenceType] = useState("photo");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    setUser(user);

    const { data: projectData } = await supabase
      .from("projects")
      .select("*")
      .eq("created_by", user.id)
      .eq("status", "approved");
    setMyProjects(projectData || []);

    const { data: supplierData } = await supabase
      .from("suppliers")
      .select("*")
      .eq("verified", true);
    setSuppliers(supplierData || []);
  };

  const handleSubmit = async () => {
    if (!selectedProject || !form.title || !form.stage_number || !form.approved_budget || !form.supplier_id || !form.invoice_amount || !form.invoice_reference) {
      setError("Please fill in all fields.");
      return;
    }
    if (!evidenceFile) {
      setError("Please attach at least one piece of evidence.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // 1. Create the milestone
      const { data: milestone, error: milestoneError } = await supabase
        .from("milestones")
        .insert({
          project_id: selectedProject,
          stage_number: Number(form.stage_number),
          title: form.title,
          description: form.description,
          approved_budget: Number(form.approved_budget),
          supplier_id: form.supplier_id,
          invoice_amount: Number(form.invoice_amount),
          invoice_reference: form.invoice_reference,
          status: "evidence_submitted",
        })
        .select()
        .single();

      if (milestoneError) throw milestoneError;

      // 2. Upload the evidence file to storage, inside the user's own folder
      const fileExt = evidenceFile.name.split(".").pop();
      const filePath = `${user.id}/${milestone.id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("evidence")
        .upload(filePath, evidenceFile);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("evidence").getPublicUrl(filePath);

      // 3. Record the evidence row, pointing at the uploaded file
      const { error: evidenceError } = await supabase.from("evidence").insert({
        milestone_id: milestone.id,
        uploaded_by: user.id,
        file_url: urlData.publicUrl,
        evidence_type: evidenceType,
      });

      if (evidenceError) throw evidenceError;

      setSuccess(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    }

    setLoading(false);
  };

  if (success) {
    return (
      <div style={{ fontFamily: "var(--font-body)", background: "#F5F0E8", minHeight: "100vh", color: "#1A1A2E" }}>
        <Navbar />
        <div style={{ maxWidth: 480, margin: "0 auto", padding: "80px 24px", textAlign: "center" }}>
          <div style={{ width: 56, height: 56, background: "rgba(46,125,50,0.08)", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 900, marginBottom: 10 }}>Milestone submitted</h2>
          <p style={{ fontSize: 14, color: "#888", lineHeight: 1.7, marginBottom: 24 }}>
            Your evidence has been recorded. Forge Vault will check it against the governance rules before any payment can be authorised.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            style={{ background: "#1B3A6B", border: "none", borderRadius: 99, padding: "13px 28px", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "var(--font-body)" }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "var(--font-body)", background: "#F5F0E8", minHeight: "100vh", color: "#1A1A2E" }}>

      <style>{`
        .milestone-header { padding: 56px 48px 40px; }
        .milestone-wrap { max-width: 560px; margin: 0 auto; padding: 0 48px 72px; }
        .milestone-card { background: #fff; border: 1px solid #EDE8DF; border-radius: 20px; padding: 32px; }
        @media (max-width: 768px) {
          .milestone-header { padding: 36px 20px 28px; }
          .milestone-wrap { padding: 0 20px 48px; }
          .milestone-card { padding: 22px; }
        }
      `}</style>

      <Navbar />

      <div className="milestone-header" style={{ background: "#fff", borderBottom: "1px solid #EDE8DF", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "rgba(27,58,107,0.07)", border: "1px solid rgba(27,58,107,0.15)", borderRadius: 99, padding: "5px 16px", fontSize: 11, color: "#1B3A6B", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>
          Submit a Milestone
        </div>
        <h1 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 900, margin: "0 0 12px", letterSpacing: "-0.02em" }}>
          Show your project's progress
        </h1>
        <p style={{ fontSize: 14, color: "#888", lineHeight: 1.7, maxWidth: 460, margin: "0 auto" }}>
          This gets checked against Forge Vault's rules before any payment can be authorised. Be accurate, this is a permanent record.
        </p>
      </div>

      <div className="milestone-wrap">
        <div className="milestone-card" style={{ marginTop: 32 }}>

          {myProjects.length === 0 ? (
            <div style={{ fontSize: 14, color: "#888", textAlign: "center", padding: "20px 0" }}>
              You don't have an approved project yet, so there's nothing to submit a milestone for.
            </div>
          ) : (
            <>
              <label style={labelStyle}>Which project is this for?</label>
              <select style={inputStyle} value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
                <option value="">Select project</option>
                {myProjects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>

              <label style={labelStyle}>Milestone title</label>
              <input style={inputStyle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Materials purchased" />

              <label style={labelStyle}>Stage number</label>
              <input
                type="number"
                style={inputStyle}
                value={form.stage_number}
                onChange={(e) => setForm({ ...form, stage_number: e.target.value })}
                placeholder="e.g. 1"
              />

              <label style={labelStyle}>Description</label>
              <textarea
                style={{ ...inputStyle, resize: "vertical" }}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What was completed at this stage?"
                rows={3}
              />

              <label style={labelStyle}>Approved budget for this stage (R)</label>
              <input
                type="number"
                style={inputStyle}
                value={form.approved_budget}
                onChange={(e) => setForm({ ...form, approved_budget: e.target.value })}
                placeholder="e.g. 25000"
              />

              <label style={labelStyle}>Supplier</label>
              <select style={inputStyle} value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
                <option value="">Select a verified supplier</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.business_name}</option>)}
              </select>
              {suppliers.length === 0 && (
                <div style={{ fontSize: 11, color: "#8a6a1a", marginTop: -12, marginBottom: 16 }}>
                  No verified suppliers exist yet. A supplier must register and be verified before they can be selected here.
                </div>
              )}

              <label style={labelStyle}>Invoice amount (R)</label>
              <input
                type="number"
                style={inputStyle}
                value={form.invoice_amount}
                onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })}
                placeholder="Must match the approved budget exactly"
              />

              <label style={labelStyle}>Invoice reference number</label>
              <input
                style={inputStyle}
                value={form.invoice_reference}
                onChange={(e) => setForm({ ...form, invoice_reference: e.target.value })}
                placeholder="e.g. INV-2026-0031"
              />

              <label style={labelStyle}>Evidence type</label>
              <select style={inputStyle} value={evidenceType} onChange={(e) => setEvidenceType(e.target.value)}>
                <option value="photo">Photo</option>
                <option value="invoice">Invoice</option>
                <option value="receipt">Receipt</option>
                <option value="contract">Contract</option>
                <option value="other">Other</option>
              </select>

              <label style={labelStyle}>Attach evidence file</label>
              <input
                type="file"
                onChange={(e) => setEvidenceFile(e.target.files[0])}
                style={{ ...inputStyle, padding: "9px 14px" }}
              />

              {error && (
                <div style={{ background: "rgba(192,57,43,0.07)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#C0392B" }}>
                  {error}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{ width: "100%", background: loading ? "#EDE8DF" : "#1B3A6B", border: "none", borderRadius: 99, padding: "14px", color: loading ? "#888" : "#fff", fontWeight: 800, fontSize: 15, cursor: loading ? "not-allowed" : "pointer", fontFamily: "var(--font-body)" }}
              >
                {loading ? "Submitting..." : "Submit Milestone"}
              </button>
            </>
          )}
        </div>
      </div>

    </div>
  );
}